import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import apiService from '../../services/apiService';
import MultiSelect from '../../components/MultiSelect';

const DMC_LOCATION_CACHE_KEY = 'dmc_locations_cache_v1';

const formatLocationName = (location) => {
  if (!location) return '';

  const stateName = location?.state?.[0]?.name || location?.stateId?.name;
  const countryName = location?.country?.[0]?.name || location?.countryId?.name;
  return [location.name, stateName, countryName].filter(Boolean).join(', ');
};

const normalizeLocations = (locationList = []) => (
  locationList.map((location) => ({
    ...location,
    name: formatLocationName(location) || location.name
  }))
);

const readDmcLocationCache = () => {
  try {
    const cached = sessionStorage.getItem(DMC_LOCATION_CACHE_KEY);
    return cached ? JSON.parse(cached) : [];
  } catch (error) {
    console.error('Failed to read DMC location cache:', error);
    return [];
  }
};

const writeDmcLocationCache = (locations = []) => {
  try {
    sessionStorage.setItem(DMC_LOCATION_CACHE_KEY, JSON.stringify(locations));
  } catch (error) {
    console.error('Failed to write DMC location cache:', error);
  }
};

const EditDmc = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const cachedLocations = !isEditMode ? readDmcLocationCache() : [];

  const [loading, setLoading] = useState(isEditMode || cachedLocations.length === 0);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    phone: '',
    email: '',
    address: '',
    domesticLocation: [],
    internationalLocation: [],
    status: '1'
  });
  const [locations, setLocations] = useState(normalizeLocations(cachedLocations));
  const internationalLocations = locations.filter((location) => location.type === 'country');

  useEffect(() => {
    fetchFormData();
  }, [id]);

  const fetchFormData = async () => {
    const hasCachedLocations = !isEditMode && cachedLocations.length > 0;

    try {
      if (!hasCachedLocations || isEditMode) {
        setLoading(true);
      }

      const response = isEditMode
        ? await apiService.get(`editDmc/${id}`)
        : await apiService.get('addDmc');

      const responseData = response?.data || {};
      const fetchedLocations = responseData.locations || [];

      if (fetchedLocations.length > 0) {
        writeDmcLocationCache(fetchedLocations);
      }

      setLocations(normalizeLocations(fetchedLocations.length > 0 ? fetchedLocations : (hasCachedLocations ? cachedLocations : [])));

      if (isEditMode) {
        const data = responseData.data;

        if (!data) {
          toast.error('DMC not found');
          navigate('/view-dmcs');
          return;
        }

        setFormData({
          id: data._id || '',
          name: data.name || '',
          phone: data.phone || '',
          email: data.email || '',
          address: data.address || '',
          domesticLocation: Array.isArray(data.domesticLocation) ? data.domesticLocation.map((item) => item._id) : [],
          internationalLocation: Array.isArray(data.internationalLocation) ? data.internationalLocation.map((item) => item._id) : [],
          status: String(data.status ?? 1)
        });
      }
    } catch (error) {
      console.error('DMC fetch error:', error);
      if (isEditMode) {
        toast.error('Failed to load DMC data');
        navigate('/view-dmcs');
      } else if (!hasCachedLocations) {
        toast.error('Failed to load DMC data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSubmitting(true);
      const response = await apiService.post('saveDmc', {
        id: formData.id,
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        address: formData.address,
        domesticLocation: formData.domesticLocation,
        internationalLocation: formData.internationalLocation,
        status: formData.status
      });

      if (response.status) {
        toast.success(response.message || `DMC ${isEditMode ? 'updated' : 'added'} successfully`);
        navigate('/view-dmcs');
      } else {
        toast.error(response.message || 'Failed to save DMC');
      }
    } catch (error) {
      console.error('DMC save error:', error);
      toast.error('Something went wrong, try again!!');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-0">
      <div className="mb-3">
        <nav className="flex flex-wrap items-center text-xs sm:text-sm">
          <Link to="/dashboard" className="text-gray-500 hover:text-gray-700 no-underline">Home</Link>
          <span className="mx-2 text-gray-400">/</span>
          <Link to="/view-dmcs" className="text-gray-500 hover:text-gray-700 no-underline">DMC Manage</Link>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-gray-900 font-medium">{isEditMode ? 'Edit DMC' : 'Add DMC'}</span>
        </nav>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#009ef7' }}></div>
          <span className="ml-2 text-gray-600">Loading...</span>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm p-6 sm:p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                DMC Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter DMC name"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="Enter phone number"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter email address"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="Enter address"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white"
              >
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Domestic Locations</label>
              <MultiSelect
                options={locations}
                value={formData.domesticLocation}
                onChange={(value) => setFormData((prev) => ({ ...prev, domesticLocation: value }))}
                placeholder="Select domestic locations"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">International Locations</label>
              <MultiSelect
                options={internationalLocations}
                value={formData.internationalLocation}
                onChange={(value) => setFormData((prev) => ({ ...prev, internationalLocation: value }))}
                placeholder="Select international locations"
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-end gap-3 mt-8">
            <button
              type="button"
              onClick={() => navigate('/view-dmcs')}
              className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50"
              style={{ backgroundColor: '#009ef7' }}
            >
              {submitting ? 'Please wait...' : (isEditMode ? 'Update DMC' : 'Save DMC')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default EditDmc;
